import ModalSelectPhoto from '@components/modal-select-photo/modal-select-photo.component'
import { Media, defaultImage } from '@hub/shared'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './photo-grid.component.scoped.scss'
import './photo-grid.component.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  size?: number
  requiredSize?: number
  featuredPhoto?: number
  onChange?: (event: any) => any
  onValidate?: (event: any) => any
  photos?: Media[]
  externalError?: any
  errorIcon?: any
  name?: string
  idProfile: string | number
  socialNetwork?: 'instagram' | 'tiktok' | 'twitter' | 'youtube'
  smallCardSize?: string
  hiddenNumberPhotos?: boolean
  label?: any
  errorAlign?: 'left' | 'right' | 'center'
}

export default ({
  size = 1,
  requiredSize = size,
  featuredPhoto = -1,
  photos = [],
  onChange,
  onValidate,
  errorIcon = <i className="fa-solid fa-exclamation-triangle"></i>,
  externalError = '',
  className = '',
  errorAlign = 'left',
  name = '',
  idProfile,
  socialNetwork = 'instagram',
  smallCardSize = '105px',
  hiddenNumberPhotos = false,
  label = '',
}: Props) => {
  const [t] = useTranslation('photoGrid')
  const timestamp = `random-id-${(1 + Date.now() + Math.random()).toString().replace('.', '')}`
  const [openSelectPhoto, setOpenSelectPhoto] = useState(false)
  const [selectedPhoto, setSelectedPhoto] = useState<Media | undefined>()
  const [indexToAdd, setIndexToAdd] = useState(-1)

  const getImageUrl = useCallback((photo: Media) => {
    if (photo?.imagens_storage?.thumbnail?.url) {
      return photo?.imagens_storage?.thumbnail?.url
    }
    if (photo?.imagens?.thumbnail?.url) {
      return photo?.imagens?.thumbnail?.url
    }
    return undefined
  }, [])

  const onOpenSelectPhoto = (photo: Media, isFeaturedPhoto = false) => {
    setSelectedPhoto({
      ...photo,
      colspan: isFeaturedPhoto ? 2 : 1,
      rowspan: isFeaturedPhoto ? 2 : 1,
    })
    setOpenSelectPhoto(true)
  }

  const updateGrid = useCallback(
    (photo: Media) => {
      const newPhotos = [...photos]
      newPhotos[indexToAdd] = {
        ...photo,
        colspan: selectedPhoto?.colspan!,
        rowspan: selectedPhoto?.rowspan!,
      }
      setIndexToAdd(-1)
      if (onValidate && typeof onValidate === 'function') {
        onValidate(newPhotos?.filter((p) => !!p)?.length === requiredSize)
      }
      if (onChange && typeof onChange === 'function') {
        onChange(newPhotos)
      }
      setSelectedPhoto(undefined)
    },
    [photos, onChange, onValidate, requiredSize, selectedPhoto, indexToAdd]
  )

  return (
    <div style={{ height: '100%' }}>
      {label ? (
        <label className="label-grid" htmlFor={name || timestamp}>
          {label}
        </label>
      ) : null}
      <ul
        className={`photo-grid ${className} ${featuredPhoto > -1 ? 'featured-photo' : ''}`}
        style={{
          gridTemplateColumns: `${Array.from(Array(size > 2 ? 3 : size).keys())
            ?.map(() => smallCardSize)
            .join(' ')}`,
        }}
      >
        {Array.from(Array(size).keys())?.map((n) => {
          {
            /* eslint-disable */
          }
          return (
            <li
              className={`photo-grid-item ${n === featuredPhoto ? 'featured-photo' : ''}`}
              key={n}
              tabIndex={0}
              role="button"
              onClick={() => {
                onOpenSelectPhoto(photos[n], n === featuredPhoto)
                setIndexToAdd(n)
              }}
              title={t('change')}
              style={{ height: n === featuredPhoto ? `calc(${smallCardSize} * 2 + 0.5rem)` : smallCardSize }}
            >
              {/* eslint-enable */}
              <figure>
                {getImageUrl(photos[n]) ? (
                  <div>
                    <span className="change-icon">
                      <i className="fa-regular fa-arrows-repeat"></i>
                    </span>
                    <img
                      src={getImageUrl(photos[n])}
                      className="img-fluid"
                      title={t('change')}
                      alt={t('change')}
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null
                        currentTarget.src = defaultImage
                      }}
                    />
                  </div>
                ) : (
                  <span className="empty-photo img-fluid">
                    <i className="fa-regular fa-image"></i>
                  </span>
                )}
                {!hiddenNumberPhotos && <span className="number">{n === featuredPhoto ? `${t('featured')} (${n + 1})` : n + 1}</span>}
              </figure>
            </li>
          )
        })}
      </ul>
      <div className={`box-validation box-invalid show error-${errorAlign}`}>
        {photos?.filter((p) => !!p)?.length < requiredSize || externalError
          ? errorIcon || <i className="fa-solid fa-exclamation-triangle"></i>
          : null}
        {externalError ? externalError : ''}
        {photos?.filter((p) => !!p)?.length < requiredSize && !externalError ? t('required') : ''}
      </div>
      <ModalSelectPhoto
        open={openSelectPhoto}
        onOpenChange={(open: boolean) => setOpenSelectPhoto(open)}
        onSelectPhoto={(newPhoto: Media) => {
          updateGrid(newPhoto)
        }}
        idProfile={idProfile}
        socialNetwork={socialNetwork}
        name={name || timestamp}
        selectedPhoto={selectedPhoto}
      />
    </div>
  )
}
